/**
 * Een tekening (PDF of afbeelding) uitlijnen op de kaart met paspunten.
 *
 * De gebruiker klikt een punt op de tekening en dan hetzelfde punt op de kaart (of typt de
 * RD-coördinaat die op de tekening staat). Met twee paspunten rekenen we een gelijkvormige
 * transformatie uit (verschuiven, draaien, schalen -- de tekening blijft recht); vanaf drie een
 * affiene (mag ook scheef/ongelijk geschaald zijn, zoals bij een scheve scan), met kleinste
 * kwadraten zodat een slordig paspunt niet alles bepaalt.
 *
 * We rekenen in RD-meters en niet in graden: een tekening is in RD recht, in graden niet.
 * Het resultaat zijn drie hoekpunten in WGS84 (linksboven, rechtsboven, linksonder); de kaart
 * in de backoffice en de veldapp tekenen de afbeelding daartussen.
 */
import { convertToWgs84, convertFromWgs84 } from '@mv/models';

/**
 * @typedef {{ u:number, v:number, X:number, Y:number }} ControlPoint  u,v = pixel op de tekening (v omlaag), X,Y = RD-meters
 * @typedef {{ a:number, b:number, c:number, d:number, e:number, f:number }} Affine  X = a·u + b·v + c ; Y = d·u + e·v + f
 */

/** Gelijkvormig (2 punten): X + iY = s·e^(iθ)·(u + i·(−v)) + t. */
function similarity(p1, p2) {
  // v wijst omlaag op een afbeelding en Y omhoog op de kaart: spiegelen.
  const du = p2.u - p1.u, dv = -(p2.v - p1.v);
  const dX = p2.X - p1.X, dY = p2.Y - p1.Y;
  const den = du * du + dv * dv;
  if (den === 0) return null;
  const re = (dX * du + dY * dv) / den; // s·cosθ
  const im = (dY * du - dX * dv) / den; // s·sinθ
  // X = re·u − im·(−v) + c  →  a = re, b = im ;  Y = im·u + re·(−v) + f  →  d = im, e = −re
  const a = re, b = im, d = im, e = -re;
  const c = p1.X - (a * p1.u + b * p1.v);
  const f = p1.Y - (d * p1.u + e * p1.v);
  return { a, b, c, d, e, f };
}

/** Kleinste-kwadraten-oplossing van een 3×3-stelsel (normaalvergelijkingen). */
function solve3(m, r) {
  const det = (q) => q[0][0] * (q[1][1] * q[2][2] - q[1][2] * q[2][1])
    - q[0][1] * (q[1][0] * q[2][2] - q[1][2] * q[2][0])
    + q[0][2] * (q[1][0] * q[2][1] - q[1][1] * q[2][0]);
  const D = det(m);
  if (Math.abs(D) < 1e-12) return null;
  return [0, 1, 2].map(k => det(m.map((row, i) => row.map((v, j) => (j === k ? r[i] : v)))) / D);
}

function affineLsq(points) {
  const N = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
  const rx = [0, 0, 0], ry = [0, 0, 0];
  for (const p of points) {
    const row = [p.u, p.v, 1];
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) N[i][j] += row[i] * row[j];
      rx[i] += row[i] * p.X; ry[i] += row[i] * p.Y;
    }
  }
  const sx = solve3(N, rx), sy = solve3(N, ry);
  if (!sx || !sy) return null;
  return { a: sx[0], b: sx[1], c: sx[2], d: sy[0], e: sy[1], f: sy[2] };
}

/**
 * Transformatie uit paspunten. Twee → gelijkvormig, drie of meer → affien. Liggen drie punten
 * op één lijn, dan valt affien weg en nemen we gelijkvormig uit de verste twee.
 * @param {ControlPoint[]} points
 * @returns {Affine|null}
 */
export function fitTransform(points) {
  if (points.length < 2) return null;
  if (points.length >= 3) {
    const t = affineLsq(points);
    if (t) return t;
  }
  let best = null, bestD = -1;
  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      const d = Math.hypot(points[i].u - points[j].u, points[i].v - points[j].v);
      if (d > bestD) { bestD = d; best = [points[i], points[j]]; }
    }
  }
  return best ? similarity(best[0], best[1]) : null;
}

export const applyTransform = (t, u, v) => ({ X: t.a * u + t.b * v + t.c, Y: t.d * u + t.e * v + t.f });

/** Afwijking per paspunt in meters: hoe goed past de uitlijning. */
export function residuals(t, points) {
  return points.map(p => {
    const q = applyTransform(t, p.u, p.v);
    return Math.hypot(q.X - p.X, q.Y - p.Y);
  });
}

/**
 * Drie hoekpunten van een afbeelding (breedte × hoogte pixels) in WGS84, als [lon, lat].
 * `system` is het stelsel waarin X/Y van de paspunten staan (standaard RD).
 */
export function cornersWgs84(t, width, height, system = 'RD') {
  const toLonLat = (u, v) => {
    const { X, Y } = applyTransform(t, u, v);
    const w = convertToWgs84(X, Y, system);
    return w ? [w.longitude, w.latitude] : null;
  };
  const topLeft = toLonLat(0, 0), topRight = toLonLat(width, 0), bottomLeft = toLonLat(0, height);
  if (!topLeft || !topRight || !bottomLeft) return null;
  return { topLeft, topRight, bottomLeft };
}

/** Een kaartklik (WGS84) → X/Y in het stelsel van de paspunten. */
export function wgs84ToSystem(lng, lat, system = 'RD') {
  const p = convertFromWgs84(lng, lat, system);
  return p ? { X: p.x, Y: p.y } : null;
}

/**
 * Pagina 1 van een PDF als afbeelding. Lang zijde max. `maxSide` pixels: scherp genoeg om op
 * in te zoomen tot op een boorpunt, klein genoeg voor de veldapp (een A0-tekening op 300 dpi
 * zou 14.000 pixels breed worden).
 *
 * @returns {Promise<{ blob: Blob, width: number, height: number, pages: number }>}
 */
export async function renderPdfPage(data, { page = 1, maxSide = 6000 } = {}) {
  const pdfjs = await import('pdfjs-dist');
  if (!pdfjs.GlobalWorkerOptions.workerSrc) {
    pdfjs.GlobalWorkerOptions.workerSrc = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
  }
  const pdf = await pdfjs.getDocument({ data: new Uint8Array(data) }).promise;
  const p = await pdf.getPage(Math.min(page, pdf.numPages));
  const base = p.getViewport({ scale: 1 });
  const scale = Math.min(8, maxSide / Math.max(base.width, base.height));
  const viewport = p.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await p.render({ canvasContext: ctx, viewport, canvas }).promise;
  const blob = await canvasToBlob(canvas);
  return { blob, width: canvas.width, height: canvas.height, pages: pdf.numPages };
}

/** Afbeelding inlezen en zo nodig verkleinen tot `maxSide`. */
export async function loadImage(file, { maxSide = 6000 } = {}) {
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, maxSide / Math.max(bmp.width, bmp.height));
  if (scale === 1 && /^image\/(png|jpeg|webp)$/.test(file.type)) {
    return { blob: file, width: bmp.width, height: bmp.height };
  }
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bmp.width * scale);
  canvas.height = Math.round(bmp.height * scale);
  canvas.getContext('2d').drawImage(bmp, 0, 0, canvas.width, canvas.height);
  return { blob: await canvasToBlob(canvas), width: canvas.width, height: canvas.height };
}

/**
 * PNG voor lijntekeningen (scherp en klein), JPEG als de PNG te groot wordt (een luchtfoto of
 * scan comprimeert als PNG slecht).
 */
async function canvasToBlob(canvas) {
  const png = await new Promise(res => canvas.toBlob(res, 'image/png'));
  if (png && png.size <= 8 * 1024 * 1024) return png;
  return new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.85));
}
